import { Fragment, type ReactNode } from 'react';

import { AudioPlayer } from './audio-player';
import { VideoEmbedPlayer } from './video-embed-player';
import type {
  ContentMediaAsset,
  TiptapBlock,
  TiptapDoc,
  TiptapMark,
} from '@/types/content';

// SPEC-021: Hand-written recursive Tiptap renderer for the mini app. The
// dashboard uses full Tiptap for editing, but the mini app only reads —
// pulling in the full editor would bloat the bundle. This walker handles
// the allowlisted block + mark set defined by
// internal/models/content.go:allowedTopLevelBlocks.

interface ArticleRendererProps {
  doc: TiptapDoc;
  media: ContentMediaAsset[];
  onProgressEvent?: (type: 'audio_progress' | 'video_open', data: Record<string, unknown>) => void;
}

export function ArticleRenderer({ doc, media, onProgressEvent }: ArticleRendererProps) {
  const assetsById = new Map(media.map((m) => [m.id, m]));
  return (
    <div className="space-y-3 px-4">
      {doc.content.map((block, i) => (
        <BlockNode
          key={i}
          block={block}
          assetsById={assetsById}
          onProgressEvent={onProgressEvent}
        />
      ))}
    </div>
  );
}

interface BlockNodeProps {
  block: TiptapBlock;
  assetsById: Map<string, ContentMediaAsset>;
  onProgressEvent?: (type: 'audio_progress' | 'video_open', data: Record<string, unknown>) => void;
}

function BlockNode({ block, assetsById, onProgressEvent }: BlockNodeProps): ReactNode {
  switch (block.type) {
    case 'heading': {
      const level = Number(block.attrs?.level ?? 2);
      const className =
        level === 2
          ? 'text-xl font-semibold leading-snug'
          : 'text-base font-semibold leading-snug';
      const children = renderInline(block.content ?? []);
      if (level === 2) return <h2 className={className}>{children}</h2>;
      if (level === 3) return <h3 className={className}>{children}</h3>;
      return <h4 className={className}>{children}</h4>;
    }

    case 'paragraph': {
      return (
        <p className="text-sm leading-relaxed text-[var(--color-fg)]">
          {renderInline(block.content ?? [])}
        </p>
      );
    }

    case 'bulletList': {
      return (
        <ul className="list-disc space-y-1 pl-5 text-sm leading-relaxed">
          {(block.content ?? []).map((li, i) => (
            <BlockNode key={i} block={li} assetsById={assetsById} onProgressEvent={onProgressEvent} />
          ))}
        </ul>
      );
    }
    case 'orderedList': {
      return (
        <ol className="list-decimal space-y-1 pl-5 text-sm leading-relaxed">
          {(block.content ?? []).map((li, i) => (
            <BlockNode key={i} block={li} assetsById={assetsById} onProgressEvent={onProgressEvent} />
          ))}
        </ol>
      );
    }
    case 'listItem': {
      return (
        <li>
          {(block.content ?? []).map((child, i) => (
            <BlockNode key={i} block={child} assetsById={assetsById} onProgressEvent={onProgressEvent} />
          ))}
        </li>
      );
    }

    case 'blockquote': {
      return (
        <blockquote className="border-l-4 border-[var(--color-accent)] pl-3 italic text-[var(--color-muted)]">
          {(block.content ?? []).map((child, i) => (
            <BlockNode key={i} block={child} assetsById={assetsById} onProgressEvent={onProgressEvent} />
          ))}
        </blockquote>
      );
    }

    case 'codeBlock': {
      return (
        <pre className="overflow-x-auto rounded-lg bg-[var(--color-secondary-bg)] p-3 text-xs">
          <code>
            {(block.content ?? []).map((n, i) => (
              <Fragment key={i}>{n.text}</Fragment>
            ))}
          </code>
        </pre>
      );
    }

    case 'horizontalRule': {
      return <hr className="my-4 border-[var(--color-separator)]" />;
    }

    case 'image': {
      const assetId = String(block.attrs?.assetId ?? '');
      const alt = String(block.attrs?.alt ?? '');
      const asset = assetsById.get(assetId);
      const src = asset?.presigned_url ?? String(block.attrs?.src ?? '');
      if (!src) return null;
      return (
        <img
          src={src}
          alt={alt}
          className="w-full rounded-xl object-cover"
          loading="lazy"
        />
      );
    }

    case 'audioBlock': {
      const assetId = String(block.attrs?.assetId ?? '');
      const asset = assetsById.get(assetId);
      if (!asset || !asset.presigned_url) {
        return (
          <div className="rounded-xl bg-[var(--color-secondary-bg)] p-3 text-xs text-[var(--color-muted)]">
            Audio unavailable.
          </div>
        );
      }
      return (
        <AudioPlayer
          src={asset.presigned_url}
          title={String(block.attrs?.title ?? 'Audio')}
          durationSec={asset.duration_sec ?? undefined}
          onProgress={(p) =>
            onProgressEvent?.('audio_progress', {
              asset_id: asset.id,
              progress: p,
            })
          }
        />
      );
    }

    case 'videoEmbed': {
      const provider = (block.attrs?.provider as 'youtube' | 'vimeo') ?? 'youtube';
      const videoId = String(block.attrs?.videoId ?? '');
      const url = String(block.attrs?.url ?? '');
      if (!videoId) return null;
      return (
        <VideoEmbedPlayer
          provider={provider}
          videoId={videoId}
          url={url}
          onOpen={() =>
            onProgressEvent?.('video_open', { provider, video_id: videoId })
          }
        />
      );
    }

    default: {
      // Unknown block type — render text content if present, otherwise drop.
      if (block.text) return <>{block.text}</>;
      return null;
    }
  }
}

// ---------------------------------------------------------------------------
// Inline renderer
// ---------------------------------------------------------------------------

function renderInline(nodes: TiptapBlock[]): ReactNode {
  return nodes.map((node, i) => {
    if (node.type !== 'text') {
      // Nested non-text inline nodes are rare in the allowlist; fall
      // through to the block renderer for completeness.
      return (
        <BlockNode key={i} block={node} assetsById={new Map()} />
      );
    }
    return (
      <Fragment key={i}>{applyMarks(node.text ?? '', node.marks ?? [])}</Fragment>
    );
  });
}

function applyMarks(text: string, marks: TiptapMark[]): ReactNode {
  let node: ReactNode = text;
  for (const mark of marks) {
    switch (mark.type) {
      case 'bold':
      case 'strong':
        node = <strong>{node}</strong>;
        break;
      case 'italic':
      case 'em':
        node = <em>{node}</em>;
        break;
      case 'code':
        node = (
          <code className="rounded bg-[var(--color-secondary-bg)] px-1 py-0.5 text-xs">
            {node}
          </code>
        );
        break;
      case 'link': {
        const href = String(mark.attrs?.href ?? '#');
        node = (
          <a
            href={href}
            target="_blank"
            rel="noreferrer noopener"
            className="text-[var(--color-link)] underline"
          >
            {node}
          </a>
        );
        break;
      }
      default:
        break;
    }
  }
  return node;
}
